import { Component, Emit, Inject, Prop, Vue } from 'vue-property-decorator'
import { Nullable } from '@/shared/types/app.types'
import {
    InvoiceStatus,
    InvoicePatchRequest,
    InvoiceResponse
} from '@/api/services/billing/common-invoice/billing-common-invoice.dto'
import { BillableDeliveryNoteResponse } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import { billingCommonInvoiceService } from '@/api/services/billing/common-invoice/billing-common-invoice.service'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { Validator } from 'vee-validate'
import { BillingOperation } from '@/api/operations/models/billing-operation'
import { HelperUtils } from '@/shared/util/helper-utils'
import { BillingOperationWithConceptsGuard } from '@/modules/billing-module/shared/guards/billing-operation-with-concepts-guard'

@Component
export default class BillingModalInvoicesCrudActions extends Vue {
    @Prop({ type: Object }) readonly invoice!: Nullable<InvoiceResponse>
    @Prop({ type: Number, default: 0 }) readonly invoiceDeliveryNotesCount!: number
    @Prop({ type: Array, default: () => [] }) readonly deliveryNotes!: BillableDeliveryNoteResponse[]

    @Inject() externalValidator!: Validator

    constructor() {
        super()
        this.$validator = this.externalValidator
    }

    @Emit()
    refresh(invoiceId: number): number {
        return invoiceId
    }

    private get _invoiceGenerated(): boolean {
        return this.invoice?.status.code === InvoiceStatus.GENERATED
    }
    private get _invoiceCancelled(): boolean {
        return this.invoice?.status.code === InvoiceStatus.CANCELLED
    }
    private get _invoiceVerified(): boolean {
        return this.invoice?.status.code === InvoiceStatus.VERIFIED
    }

    private get _deliveryNotesIds(): number[] {
        return this.deliveryNotes.map(deliveryNote => deliveryNote.id)
    }

    private get _numberOfConcepts(): number {
        if (this.invoice) {
            return this.invoice.appliedBillableConceptsCount
        }
        return this.deliveryNotes.reduce((accumulator, deliveryNote) => {
            return accumulator + deliveryNote.numberOfconcepts
        }, 0)
    }

    get visibleCreateInvoice(): boolean {
        return !this.invoice &&
            this._deliveryNotesIds.length > 0
    }
    get visibleUpdateInvoice(): boolean {
        return !!this.invoice
    }

    get visibleCancelInvoice(): boolean {
        return this._invoiceGenerated
    }
    get visibleVerifyInvoice(): boolean {
        return this._invoiceGenerated &&
            this.invoiceDeliveryNotesCount > 0
    }

    get visibleReplaceReport(): boolean {
        return this._invoiceVerified &&
            HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.REPLACE_INVOICE_REPORTS)
    }

    async onCreateInvoice(): Promise<void> {
        await BillingOperationWithConceptsGuard.run(
            this._numberOfConcepts,
            async () => {
                const response =
                    await PromiseUtils.toCallSecure(
                        billingCommonInvoiceService.create(store.state.labId, this._deliveryNotesIds))
                if (response !== null) {
                    ToastUtils.showToastSuccess(this.$tc('accounting.invoice.generated'))
                    this.refresh(response.id) // emits an event
                }
            }
        )
    }
    private async _updateInvoice(): Promise<boolean> {
        const valid = await this.$validator.validateAll()
        if (valid && this.invoice) {
            const { id, realTotalValue, realAccountingUnit, externalReference, sentToGovernmentRevenueServices, paymentDate, paymentMethod } = this.invoice
            const request: InvoicePatchRequest = {
                id,
                realTotalValue: this._invoiceGenerated ? realTotalValue : undefined,
                realAccountingUnitId: this._invoiceGenerated ? realAccountingUnit?.id : undefined,
                externalReference: this._invoiceVerified || this._invoiceCancelled ? externalReference : undefined,
                sentToGovernmentRevenueServices: this._invoiceVerified || this._invoiceCancelled ? sentToGovernmentRevenueServices : undefined
            }
            const response =
                await PromiseUtils.toCallSecure(
                    billingCommonInvoiceService.update(store.state.labId, request))
            return response !== null
        }
        return false
    }
    async onUpdateInvoice(): Promise<void> {
        if (this.invoice && await this._updateInvoice()) {
            ToastUtils.showToastSuccess(this.$tc('accounting.invoice.updated'))
            this.refresh(this.invoice.id) // emits an event
        }
    }

    async onCancelInvoice(): Promise<void> {
        await BillingOperationWithConceptsGuard.run(
            this._numberOfConcepts,
            async () => {
                if (this.invoice) {
                    const { id } = this.invoice
                    const response =
                        await PromiseUtils.toCallSecure(
                            billingCommonInvoiceService.cancel(store.state.labId, id))
                    if (response !== null) {
                        ToastUtils.showToastSuccess(this.$tc('accounting.invoice.cancelled'))
                        this.refresh(id) // emits an event
                    }
                }
            }
        )
    }
    async onVerifyInvoice(): Promise<void> {
        await BillingOperationWithConceptsGuard.run(
            this._numberOfConcepts,
            async () => {
                if (this.invoice) {
                    const { id, realTotalValue, realAccountingUnit, conceptsTotalValue, conceptsAccountingUnit } = this.invoice
                    if (!realTotalValue && !realAccountingUnit) {
                        this.invoice.realTotalValue = conceptsTotalValue
                        this.invoice.realAccountingUnit = conceptsAccountingUnit
                    }

                    if (await this._updateInvoice()) {
                        const response =
                            await PromiseUtils.toCallSecure(
                                billingCommonInvoiceService.verify(store.state.labId, id))
                        if (response !== null) {
                            ToastUtils.showToastSuccess(this.$tc('accounting.invoice.verified'))
                            this.refresh(id) // emits an event
                        }
                    }
                }
            }
        )
    }
    async onReplaceReport(): Promise<void> {
        if (this.invoice) {
            const { id } = this.invoice
            const response =
                    await PromiseUtils.toCallSecure(
                        billingCommonInvoiceService.replaceReport(store.state.labId, id))
            if (response !== null) {
                ToastUtils.showToastSuccess(this.$tc('accounting.invoice.invoiceReportReplaced'))
                this.refresh(id) // emits an event
            }
        }
    }
}
